import React, { useState, useEffect, useCallback } from "react";
import { Save, ShieldCheck, Zap, Globe, Copy, Trash2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const TelegramConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [telegramConfig, setTelegramConfig] = useState({
    bot_token: "",
    is_active: false,
    streaming_enabled: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/telegram/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setTelegramConfig({
          bot_token: response.config.bot_token ? UNCHANGED_KEY : "",
          is_active: response.config.is_active || false,
          streaming_enabled: response.config.streaming_enabled || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.log("Telegram config not found");
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!telegramConfig.bot_token.trim()) {
      showToast("Please enter your Bot Token", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/telegram/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify(telegramConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Telegram configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/telegram", {
        method: "DELETE"
      });
      if (response.success) {
        setTelegramConfig({
          bot_token: "",
          is_active: false,
          streaming_enabled: false,
        });
        setWebhookUrl("");
        showToast("Telegram integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith('http://') || webhookUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api')) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!telegramConfig.is_active) {
      if (!telegramConfig.bot_token?.trim()) {
        showToast("Please enter and save your Bot Token first", "error");
        return;
      }
      if (telegramConfig.bot_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !telegramConfig.is_active;
      const response = await ApiClient.request("/telegram/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setTelegramConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const toggleStreaming = async () => {
    try {
      const newStreamingState = !telegramConfig.streaming_enabled;
      const response = await ApiClient.request("/telegram/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: newStreamingState }),
      });

      if (response.success) {
        setTelegramConfig((prev) => ({ ...prev, streaming_enabled: newStreamingState }));
        showToast("Streaming status updated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Telegram Sync"
          description="Dispatch neural intelligence directly to your users' chat stream via the Telegram bot protocol."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${telegramConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {telegramConfig.is_active ? "Operational" : "Offline"}
              </span>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={telegramConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Bot Credentials</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Telegram bot</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Bot Token
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : telegramConfig.bot_token}
                    onChange={(e) =>
                      setTelegramConfig({
                        ...telegramConfig,
                        bot_token: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (telegramConfig.bot_token === UNCHANGED_KEY) {
                        setTelegramConfig({ ...telegramConfig, bot_token: "" });
                      }
                    }}
                    placeholder={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : "Enter your Telegram Bot Token"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  You can obtain this token from <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-gray-500">@BotFather</a> on Telegram. Send the <code>/newbot</code> command to create a new bot.
                </p>
              </div>

              {/* Streaming Responses Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-gray-900">Streaming Responses</h4>
                  <p className="text-[11px] font-medium text-gray-400">Stream response tokens as they are generated using editMessageText typewriter edits.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={telegramConfig.streaming_enabled}
                    onChange={toggleStreaming}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-gray-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-gray-900"></div>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                {telegramConfig.bot_token && (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
                  >
                    <Trash2 size={18} />
                    Delete Integration
                  </button>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving || !telegramConfig.bot_token?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
                >
                  <Save size={18} />
                  {saving ? "Refining..." : "Save Bot Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Endpoint</h3>
                  <p className="text-[11px] font-medium text-gray-400">Real-time message delivery endpoint</p>
                </div>
              </div>

              <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Live Webhook URL
                </label>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-900 hover:text-white rounded-xl text-[10px] font-bold text-gray-500 transition-all border border-gray-100 shadow-sm"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-400">
                This URL is automatically registered with Telegram's API upon saving your configuration.
              </p>
            </div>
          )}
        </div>

      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Delete Telegram Integration"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Delete Integration"
        isLoading={saving}
      />
    </div>
  );
};

export default TelegramConfig;
